import { requireCompletedProfile } from "@hisani/auth";
import { canRequestEntityOwnerTransfer, isEligibleEntityOwner, } from "../domain/membership/authorization-policy.js";
import { generateStablePublicId, normalizeStablePublicId, } from "../domain/stable/public-id.js";
import { stableConflict, stableForbidden, stableNotFound, stableRateLimited, createStableSchema, getStableSchema, lookupStablePublicIdSchema, requestEntityOwnerTransferSchema, respondEntityOwnerTransferSchema, } from "../validation.js";
export async function createStable(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = createStableSchema.parse(input);
    const now = dependencies.clock.now();
    return dependencies.repository.createStable({
        id: dependencies.newId(),
        publicId: generateStablePublicId(dependencies.random),
        name: parsed.name,
        countryCode: parsed.countryCode ?? null,
        creatorHorsemanId: actor.account.id,
        now,
    });
}
export async function listMyStables(principal, dependencies) {
    const actor = requireCompletedProfile(principal);
    return dependencies.repository.listStablesForHorseman(actor.account.id);
}
export async function getStableDashboard(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = getStableSchema.parse(input);
    const dashboard = await dependencies.repository.getStableDashboard(parsed.stableId, actor.account.id);
    if (dashboard === null)
        throw stableNotFound();
    return dashboard;
}
/**
 * Exact-match Stable ID lookup for Stable Transfer initiation
 * (ARCHITECTURE.md §13.2). Rate-limited per actor; returns only minimum
 * confirmation data. The Transfer workflow itself is a later phase — this
 * lookup capability is the foundational, cheap-to-build seam it depends on.
 */
export async function lookupStablePublicId(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = lookupStablePublicIdSchema.parse(input);
    const now = dependencies.clock.now();
    const policy = await dependencies.policy.get();
    const windowStart = new Date(now.getTime() - 60 * 60 * 1000);
    const { attemptsInWindow } = await dependencies.repository.recordPublicIdLookupAttempt({
        actorId: actor.account.id,
        now,
        windowStart,
    });
    if (attemptsInWindow > policy.publicIdLookupRateLimitPerHour) {
        throw stableRateLimited();
    }
    const stable = await dependencies.repository.findStableByPublicId(normalizeStablePublicId(parsed.publicId));
    if (stable === null) {
        return { status: "NOT_FOUND" };
    }
    const activeHorseCount = await dependencies.repository.countActiveHorses(stable.id);
    return { status: "FOUND", name: stable.name, activeHorseCount };
}
export async function requestEntityOwnerTransfer(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = requestEntityOwnerTransferSchema.parse(input);
    const membership = await dependencies.repository.getMembershipContext(parsed.stableId, actor.account.id);
    const dashboard = await dependencies.repository.getStableDashboard(parsed.stableId, actor.account.id);
    if (membership === null || dashboard === null)
        throw stableNotFound();
    if (!canRequestEntityOwnerTransfer(membership, dashboard.stable.entityOwnerHorsemanId)) {
        throw stableForbidden();
    }
    const recipientMembership = await dependencies.repository.getMembershipContext(parsed.stableId, parsed.toHorsemanId);
    if (recipientMembership === null ||
        !isEligibleEntityOwner(recipientMembership.entityRole)) {
        throw stableConflict("The Entity Owner tag can only transfer to a current Trainer, Co-Trainer, or Racing Manager.");
    }
    const now = dependencies.clock.now();
    const transfer = await dependencies.repository.requestEntityOwnerTransfer({
        id: dependencies.newId(),
        stableId: parsed.stableId,
        fromHorsemanId: actor.account.id,
        toHorsemanId: parsed.toHorsemanId,
        now,
    });
    if (transfer === null) {
        throw stableConflict("An Entity Owner transfer is already pending.");
    }
    return transfer;
}
export async function respondEntityOwnerTransfer(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = respondEntityOwnerTransferSchema.parse(input);
    const now = dependencies.clock.now();
    const transfer = await dependencies.repository.respondEntityOwnerTransfer({
        transferId: parsed.transferId,
        actorId: actor.account.id,
        decision: parsed.decision,
        now,
    });
    if (transfer === null)
        throw stableConflict();
    return transfer;
}
